import { ThemedStyledFunction } from 'styled-components';
import WrapperInterface from './WrapperInterface';
import { getBackgroundStyleFromImage } from './GraphicsUtils';
import { callFunction } from './FirebaseUtils';
import FileType from '../data/FileType';
import { getAudioContext } from '../audio/Context';
import AppVersion from '../data/AppVersion';
import {
  getWrapperVersion,
  wrapperVersionWithoutGIFCompression
} from '../data/AppVersion';
import * as Fingerprint2 from 'fingerprintjs2';
import User from '../data/User';
import Storable from '../data/Storable';
import MP3Converter from '../audio/MP3Converter';
import { isUrlOnAWS } from './S3Utils';
import { getAudioBuffer } from '../audio/AudioUtils';
import { reportBFError } from './Tracker';
import BackgroundStyle from '../data/BackgroundStyle';
import { RGBColor } from 'react-color';
import { IModifiers } from '../data/IModifiers';
import { PointData } from '../data/FlyerData';
import Plan from 'buy/Plan';
import toWav from 'audiobuffer-to-wav';

const FileSelector = (window as any).FileSelector;

export const getValueFromLocalStorage = (key: string): string | null => {
  if (typeof window.localStorage !== 'undefined') {
    return localStorage.getItem(key);
  }
  return null;
};

export const setValueInLocalStorage = (key: string, value: string) => {
  if (typeof window.localStorage !== 'undefined') {
    localStorage.setItem(key, value);
  }
};

interface SelectableFile {
  type: string;
}

export const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (error: any) => {
      reject(error);
    };
    img.src = url;
  });
};

export const loadFile = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('GET', url, true);
    x.responseType = 'blob';
    x.onload = () => {
      resolve(x.response);
    };
    x.onerror = (error: any) => {
      reject(error);
    };
    x.send();
  });
};

export const getFileSize = (url: string): Promise<number> => {
  return new Promise((resolve: Function, reject: Function) => {
    let http = new XMLHttpRequest();
    http.open('HEAD', url, true); // true = Asynchronous
    http.onreadystatechange = () => {
      if (http.readyState === XMLHttpRequest.DONE && http.status === 200) {
        const size = parseInt(http.getResponseHeader('content-length')!, 10);
        resolve(size);
      }
    };
    http.onerror = (error: any) => {
      reject(error);
    };
    http.send();
  });
};

export const getBackgroundStyleFromImageURL = (
  url: string
): Promise<BackgroundStyle> => {
  return new Promise(
    (
      resolve: (style: BackgroundStyle) => void,
      reject: (e: ErrorEvent) => void
    ) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const type = getBackgroundStyleFromImage(img);
        resolve(type);
      };
      img.onerror = (e: ErrorEvent) => {
        reject(e);
      };
      img.src = url;
    }
  );
};

export const getBackgroundStyleFromImageFile = async (
  file: File
): Promise<BackgroundStyle> => {
  const url = URL.createObjectURL(file);
  const type = await getBackgroundStyleFromImageURL(url);
  URL.revokeObjectURL(url);
  return type;
};

export const getIndexWithFloatPosition = (
  length: number,
  position: number
): number => {
  let index = 0;
  if (position === 1) {
    index = length - 1;
  } else {
    index = Math.floor(position * length);
  }
  return index;
};

export const smoothArray = (array: number[], smoothness: number) => {
  const len = array.length;
  const result = [];
  for (let i = 0; i < len; i++) {
    let valuesCount = 1;
    let total = array[i];
    for (let j = 0; j < smoothness; j++) {
      if (i - j >= 0) {
        total += array[i - j];
        valuesCount++;
      }
      if (i + j < len) {
        total += array[i + j];
        valuesCount++;
      }
    }
    result.push(total / valuesCount);
  }
  return result;
};

export const getMinValue = (items: number[]) => {
  if (items.length) {
    const length = items.length;
    let result = items[0];
    for (let i = 0; i < length; i++) {
      result = Math.min(result, items[i]);
    }
    return result;
  } else {
    console.log("Can't get MIN value of empty array");
    return 0;
  }
};

export const getMaxValue = (items: number[]) => {
  if (items.length) {
    const length = items.length;
    let result = items[0];
    for (let i = 0; i < length; i++) {
      result = Math.max(result, items[i]);
    }
    return result;
  } else {
    console.log("Can't get MAX value of empty array");
    return 0;
  }
};

export const normalizeArray = (array: number[]): number[] => {
  const min = getMinValue(array);
  const max = getMaxValue(array);
  const delta = max - min;
  const result = [];
  const length = array.length;
  for (let i = 0; i < length; i++) {
    result.push((array[i] - min) / delta); // performance
  }
  return result;
};

let canUseWebGLValue: boolean;
export const canUseWebGL = (): boolean => {
  if (canUseWebGLValue === undefined) {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    canUseWebGLValue = gl !== null;
  }
  return canUseWebGLValue;
};

export const canUseWebGL2 = (): boolean => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');
  return gl !== null;
};

export const canBrowserPlayAudio = (): boolean => {
  return true;
  // const reg = /^((?!chrome|android).)*safari/i;
  // return !reg.test(navigator.userAgent);
};

export const canRecordVideoSync = (): boolean => {
  return !!(
    (getAudioContext() as any).createMediaStreamDestination &&
    ((window as any).MediaRecorder?.isTypeSupported(
      'video/webm;codecs=vp8,opus'
    ) ||
      (window as any).MediaRecorder?.isTypeSupported('video/mp4'))
  );
};

export const canRecordVideoAsync = (): boolean => {
  // from https://stackoverflow.com/questions/5573096/detecting-webp-support
  var elem = document.createElement('canvas');
  if (!!(elem.getContext && elem.getContext('2d'))) {
    // was able or not to get WebP representation
    try {
      const url = elem.toDataURL('image/webp');
      return url.indexOf('data:image/webp') === 0;
    } catch (e) {
      reportBFError(e);
    }
  }
  // very old browser like IE 8, canvas not supported
  return false;
};

export const canRecordVideo = (): boolean => {
  return (
    canRecordVideoAsync() ||
    canRecordVideoSync() ||
    (window as any).isBeatflyerLite
  );
};

export const isBrowserFullySupported = (): boolean => {
  return canBrowserPlayAudio() && canUseWebGL2() && canRecordVideo();
};

// export const isBrowserSupported = (): boolean => {
//   return false;
//   // return (
//   //   /Chrome/.test(navigator.userAgent) &&
//   //   /Google Inc/.test(navigator.vendor) &&
//   //   (window as any).MediaRecorder !== undefined
//   // ); // && window.OfflineAudioContext !== undefined;
// };

export const isSupportedFileImage = (file: SelectableFile): boolean => {
  return (
    ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].indexOf(file.type) >
    -1
  );
};

export const isSupportedFileAudio = (file: SelectableFile): boolean => {
  return (
    [
      'audio/mp3',
      'audio/wav',
      'audio/x-wav',
      'audio/m4a',
      'audio/x-m4a',
      'audio/aac',
      'audio/mpeg'
    ].indexOf(file.type) > -1
  );
};

export const isSupportedFileVideo = (file: SelectableFile): boolean => {
  return file.type === FileType.mp4;
};

export const isSettingFile = (file: SelectableFile): boolean => {
  let matchExtension = true;
  if (file instanceof File) {
    matchExtension =
      ((file as File).name.split('.').pop() as string).toLowerCase() === 'json';
  }
  return (
    matchExtension && (file.type === 'application/json' || file.type === '')
  );
};

export const isSupportedFileOther = (file: SelectableFile): boolean => {
  return isSettingFile(file);
};

export const isSupportedFile = (file: SelectableFile): boolean => {
  return (
    isSupportedFileImage(file) ||
    isSupportedFileAudio(file) ||
    isSupportedFileVideo(file) ||
    isSupportedFileOther(file)
  );
};

export const generateClassName = (
  base: string,
  modifiers: IModifiers | undefined
): string => {
  if (!modifiers) {
    return base;
  }
  let result = base;
  const re = /[A-Z]/;
  for (let key in modifiers) {
    if (modifiers[key] === true) {
      if (key !== key.toLowerCase()) {
        // replace camelcase with '-...'
        var match = re.exec(key);
        while (match) {
          key =
            key.substr(0, match.index) +
            '-' +
            match[0].toLowerCase() +
            key.substr(match.index + 1, key.length);
          match = re.exec(key);
        }
      }
      result += ' ' + base + '--' + key;
    }
  }
  return result;
};

export const isUrlBlob = (url: string): boolean => {
  return url.startsWith('blob:');
};

export const newUUID = (): string => {
  return (window as any).Uuid.raw();
};

export const isUUID = (value: string): boolean => {
  return (window as any).Uuid.isUuid(value);
};

export const cloneURL = async (url: string): Promise<string> => {
  if (isUrlBlob(url)) {
    const blob = await loadFile(url);
    return URL.createObjectURL(blob);
  } else {
    const firstPart = url.split('?')[0];
    return `${firstPart}?rand=${newUUID()}`;
  }
};

export const isSuperUser = (user: User | null): boolean => {
  return (
    user !== null &&
    user!.email !== undefined &&
    (user.email === 'nuthinking@gmail.com' ||
      user.email === 'beatflyerdemo@gmail.com')
  );
};

export const isPro = (user: User | null): boolean => {
  return user?.plan?.startsWith('pro') === true && user?.isBlocked !== true;
};

export const canUserSave = (user: User | null): boolean => {
  return isPro(user) && user!.plan !== Plan.ProDay;
};

export const isRunningInProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

export const styledWithProps =
  <U>() =>
  <P, T, O>(
    fn: ThemedStyledFunction<P, T, O>
  ): ThemedStyledFunction<P & U, T, O & U> =>
    fn as any;

export const isElectronApp = (): boolean => {
  return (window as any).electron !== undefined;
};

export const getWrapperInterface = (): WrapperInterface | null => {
  return (window as any).beatflyerWrapperInterface;
};

export const canCompressGIFsLocally = (): boolean => {
  return (
    isElectronApp() &&
    getWrapperVersion().isNewerThan(wrapperVersionWithoutGIFCompression)
  );
};

// let debugImagesContainer: HTMLElement | null = null;
export const debugImage = (url: string) => {
  if ((console as any).image) {
    (console as any).image(url);
  } else {
    console.warn('Console.image is not available');
  }
  // if (!debugImagesContainer) {
  //   debugImagesContainer = document.createElement('div');
  //   debugImagesContainer.style.position = 'fixed';
  //   debugImagesContainer.style.left = '0';
  //   debugImagesContainer.style.top = '0';
  //   debugImagesContainer.style.zIndex = '100000';
  //   document.body.appendChild(debugImagesContainer);
  // }
  // const image = new Image();
  // image.src = url;
  // image.style.width = '200px';
  // image.style.height = 'auto';
  // image.style.display = 'block';
  // image.style.background = 'rgba(0,0,0, 0.5)';
  // image.style.marginBottom = '2px';
  // debugImagesContainer.appendChild(image);
  // // var w: Window = window.open('')!;
  // // w.document.write(image.outerHTML);
};

export const getLatestElectronWrapperVersion =
  async (): Promise<AppVersion> => {
    const response = await fetch(
      'https://www.beatflyer.com/api/latest-version'
    );
    const value = await response.text();
    return new AppVersion(value);
  };

export const timeoutPromise = (ms: number = 0) =>
  new Promise(res => setTimeout(res, ms));

const COUNTRY_CODE_STORAGE_KEY = 'countryCode';
export const fetchIPData = async (): Promise<any> => {
  const response = await fetch(
    '//ip-api.io/json/?api_key=42a78aca-aa82-4ad6-a05a-4dffecb9459a'
  );
  return await response.json();
};

export const getCountryByIP = async (): Promise<string | null> => {
  let countryCode = getValueFromLocalStorage(COUNTRY_CODE_STORAGE_KEY);
  if (countryCode) {
    console.log('COUNTRY CODE ALREADY PRESENT!');
    return countryCode;
  }
  try {
    const data = await fetchIPData();
    countryCode = data.country_code;
    setValueInLocalStorage(COUNTRY_CODE_STORAGE_KEY, countryCode!);
    return countryCode!;
  } catch (e) {
    reportBFError(e);
    return null;
  }
};

export const getIP = async (): Promise<string | null> => {
  try {
    const data = await fetchIPData();
    // TODO: check if the format is correct xxx.xxx.xxx.xxx
    return data.ip;
  } catch (e) {
    reportBFError(e);
    return null;
  }
};

export const getCurrencyByCountryCode = async (
  countryCode: string | null
): Promise<string> => {
  if (countryCode === null) {
    return '$';
  }
  return (await callFunction('getCurrency', countryCode)).data;
};

export const getFingerPrint = async (): Promise<string> => {
  let options = {
    excludes: {
      colorDepth: true,
      screenResolution: true,
      pixelRatio: true,
      availableScreenResolution: true,
      enumerateDevices: true
    }
  };
  let components = await Fingerprint2.getPromise(options);
  // console.log(components);
  var values = components.map(function (component: Fingerprint2.Component) {
    return component.value;
  });
  return Fingerprint2.x64hash128(values.join(''), 31);
};

export const printObject = (o: any, indent: number | undefined = undefined) => {
  var out = '';
  if (typeof indent === 'undefined') {
    indent = 0;
  }
  for (var p in o) {
    if (o.hasOwnProperty(p)) {
      var val = o[p];
      out += new Array(4 * indent + 1).join(' ') + p + ': ';
      if (typeof val === 'object') {
        if (val instanceof Date) {
          out += 'Date "' + val.toISOString() + '"';
        } else {
          out +=
            '{\n' +
            printObject(val, indent + 1) +
            new Array(4 * indent + 1).join(' ') +
            '}';
        }
      } else if (typeof val === 'function') {
        // do nothing
      } else {
        out += '"' + val + '"';
      }
      out += ',\n';
    }
  }
  return out;
};

export const readJSONFile = (file: File): Promise<any> => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.addEventListener('load', (e: any) => {
      let settings: any = null;
      try {
        settings = JSON.parse(e.target.result);
      } catch (err) {
        alert(err);
      } finally {
        resolve(settings);
      }
    });
    reader.readAsBinaryString(file);
  });
};

export const isExtensionImage = (extension: string): boolean => {
  const ext = extension.toLocaleLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'png':
      return true;
    default:
      return false;
  }
};

export const isStoredAsset = (asset: Storable): boolean => {
  return (
    asset.file === null &&
    asset.url !== null &&
    !isUrlBlob(asset.url) &&
    isUrlOnAWS(asset.url)
  );
};

export const createWaveBlob = (abuffer: AudioBuffer) => {
  var numOfChan = abuffer.numberOfChannels,
    length = abuffer.length * numOfChan * 2 + 44,
    buffer = new ArrayBuffer(length),
    view = new DataView(buffer),
    channels = [],
    i,
    sample,
    offset = 0,
    pos = 0;

  const setUint16 = (data: number) => {
    view.setUint16(pos, data, true);
    pos += 2;
  };

  const setUint32 = (data: number) => {
    view.setUint32(pos, data, true);
    pos += 4;
  };

  // write WAVE header
  setUint32(0x46464952); // "RIFF"
  setUint32(length - 8); // file length - 8
  setUint32(0x45564157); // "WAVE"

  setUint32(0x20746d66); // "fmt " chunk
  setUint32(16); // length = 16
  setUint16(1); // PCM (uncompressed)
  setUint16(numOfChan);
  setUint32(abuffer.sampleRate);
  setUint32(abuffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
  setUint16(numOfChan * 2); // block-align
  setUint16(16); // 16-bit (hardcoded in this demo)

  setUint32(0x61746164); // "data" - chunk
  setUint32(length - pos - 4); // chunk length

  // write interleaved data
  for (i = 0; i < abuffer.numberOfChannels; i++) {
    channels.push(abuffer.getChannelData(i));
  }

  while (pos < length) {
    for (i = 0; i < numOfChan; i++) {
      // interleave channels
      sample = Math.max(-1, Math.min(1, channels[i][offset])); // clamp
      // tslint:disable-next-line:no-bitwise
      sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0; // scale to 16-bit signed int
      view.setInt16(pos, sample, true); // write 16-bit sample
      pos += 2;
    }
    offset++; // next source sample
  }

  // create Blob
  return new Blob([buffer], { type: 'audio/wav' });
};

const convertToMp3 = async (file: File): Promise<File> => {
  const converter = new MP3Converter();
  const blob = await converter.convert(file);
  return new File([blob], 'soundtrack.mp3');
};

export const compressAudioFile = async (
  file: File,
  forceMP3: boolean = false
): Promise<File> => {
  const wi = getWrapperInterface();
  if (!forceMP3 && isElectronApp() && wi) {
    const buffer = await getAudioBuffer(file);
    let blob: Blob | undefined;
    if (wi.compressAudioWithWav) {
      let wav = toWav(buffer);
      blob = await wi.compressAudioWithWav(wav);
    } else if (wi.compressAudio) {
      blob = await wi.compressAudio(buffer);
    }
    if (blob) {
      return new File([blob], 'soundtrack.mp4');
    }
  }
  return convertToMp3(file);
};

export const preloadUrls = async (urls: string[]) => {
  while (urls.length) {
    const url = urls.shift() as string;
    await loadFile(url);
  }
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
  var arr = dataurl.split(',');
  var mime = (arr[0].match(/:(.*?);/) as any)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const getCSSVarValue = (varName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

export const setCSSVar = (varName: string, value: string) => {
  document.documentElement.style.setProperty(varName, value);
};

export const toggleClass = (
  element: HTMLElement,
  className: string,
  add: boolean = true
) => {
  element.classList.toggle(className, add);
};

export const selectLocalFiles = async (
  type: FileType,
  multiple: boolean = false
): Promise<File[]> => {
  var selector = new FileSelector();
  switch (type) {
    case FileType.Graphic:
      selector.accept = '.png,.jpg,.jpeg,.mp4,.gif';
      break;
    case FileType.Image:
      selector.accept = '.png,.jpg,.jpeg,.gif';
      break;
    case FileType.Audio:
      selector.accept = '.wav,.mp3,.m4a,.aac';
      break;
    case FileType.Font:
      selector.accept = '.ttf,.otf';
      break;
    default:
      break;
  }
  return new Promise((resolve: (files: File[]) => void) => {
    if (multiple) {
      selector.selectMultipleFiles(
        (files: File[]) => {
          // sort files by name
          files.sort((a: File, b: File): number => {
            if (a.name < b.name) {
              return -1;
            }
            return 1;
          });
          resolve(files.filter(f => f && f.size));
        },
        () => {
          resolve([]);
        }
      );
    } else {
      selector.selectSingleFile(
        (file: File) => {
          if (!file || !file.size) {
            resolve([]);
            return;
          }
          resolve([file]);
        },
        () => {
          resolve([]);
        }
      );
    }
  });
};

export const pause = async (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const hexToRgb = (hex: string): RGBColor => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  // var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  // hex = hex.replace(shorthandRegex, function (m, r, g, b) {
  //   return r + r + g + g + b + b;
  // });

  const regex =
    hex.length === 7
      ? /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
      : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  const result = regex.exec(hex);
  const factor = 1 / 255;
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: result[4] !== undefined ? parseInt(result[4], 16) * factor : 1
      }
    : {
        r: 0,
        g: 0,
        b: 0,
        a: 1
      };
};

export const extractAlphaFromHex = (hex: string): [string, number] => {
  if (hex.length > 7) {
    return [hex.substr(0, 7), (parseInt(hex.substr(7), 16) * 1) / 255];
  } else {
    return [hex, 1];
  }
};

export const hasParentWithClassName = (
  el: HTMLElement,
  className: string,
  onlyParent: boolean = false
): boolean => {
  if (!onlyParent && el.classList.contains(className)) {
    return true;
  }
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    if (parent.classList.contains(className)) {
      return true;
    }
    parent = parent?.parentElement;
  }
  return false;
};

export const rotatePoint = (
  p: PointData,
  center: PointData,
  angle: number
): PointData => {
  const tx = center.x - p.x;
  const ty = center.y - p.y;
  const radius = Math.sqrt(Math.abs(tx * tx) + Math.abs(ty * ty));
  const a = -(Math.atan2(tx, ty) * 180) / Math.PI;
  const newAngle = angle + a;
  const newAngleRadians = (newAngle * Math.PI) / 180;
  return {
    x: center.x + Math.sin(newAngleRadians) * radius,
    y: center.y - Math.cos(newAngleRadians) * radius
  };
};

const getTriangleArea = (
  p1: PointData,
  p2: PointData,
  p3: PointData
): number => {
  return (
    Math.abs(
      p2.x * p1.y -
        p1.x * p2.y +
        (p3.x * p2.y - p2.x * p3.y) +
        (p1.x * p3.y - p3.x * p1.y)
    ) / 2
  );
};

export const rotatedRectangleContainsPoint = (
  rect: PIXI.Rectangle,
  rotation: number,
  x: number,
  y: number
): boolean => {
  const center: PointData = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
  let topLeft: PointData = {
    x: rect.x,
    y: rect.y
  };
  let topRight: PointData = {
    x: rect.right,
    y: rect.y
  };
  let bottomLeft: PointData = {
    x: rect.x,
    y: rect.bottom
  };
  let bottomRight: PointData = {
    x: rect.right,
    y: rect.bottom
  };
  topLeft = rotatePoint(topLeft, center, rotation);
  topRight = rotatePoint(topRight, center, rotation);
  bottomLeft = rotatePoint(bottomLeft, center, rotation);
  bottomRight = rotatePoint(bottomRight, center, rotation);

  const p: PointData = {
    x,
    y
  };
  // formula from https://stackoverflow.com/questions/17136084/checking-if-a-point-is-inside-a-rotated-rectangle/17146376
  const areaTop = getTriangleArea(topLeft, topRight, p);
  const areaRight = getTriangleArea(topRight, bottomRight, p);
  const areaBottom = getTriangleArea(bottomLeft, bottomRight, p);
  const areaLeft = getTriangleArea(topLeft, bottomLeft, p);
  const diff =
    areaTop + areaRight + areaBottom + areaLeft - rect.width * rect.height;
  return diff <= 10;
};

export const isIOS = (): boolean => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const openURLFromLite = (url: string) => {
  if ((window as any).cep) {
    (window as any).cep.util.openURLInDefaultBrowser(url);
  } else if ((window as any).beatflyerPlatform === 'sketch') {
    window.top?.postMessage(
      {
        type: 'openLink',
        url
      },
      '*'
    );
  } else {
    window.open(url, '_blank');
  }
};

export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const isEditingText = (): boolean => {
  const { activeElement } = document;
  if (!activeElement) {
    return false;
  }
  if (activeElement.tagName.toLowerCase() === 'textarea') {
    return true;
  }
  if (
    activeElement.tagName.toLowerCase() === 'input' &&
    (activeElement as HTMLInputElement).type === 'text'
  ) {
    return true;
  }
  return false;
};

export const isPickingColor = (container?: HTMLElement): boolean => {
  const el = container ?? document;
  return el.querySelector('.chrome-picker') !== null;
};

export const cloneObject = <T>(x: T): T => {
  return JSON.parse(JSON.stringify(x));
};

export const loadScript = (url: string): Promise<void> => {
  return new Promise(resolve => {
    var script = document.createElement('script');
    script.src = url;

    script.onload = function () {
      resolve();
    };

    document.head.appendChild(script);
  });
};

const CUSTOM_FONT_PREFIX = '-bf-custom ';
export const makeFontNameCustom = (fontName: string): string => {
  return CUSTOM_FONT_PREFIX + fontName;
};
export const isCustomFont = (fontName: string): boolean => {
  return fontName.startsWith(CUSTOM_FONT_PREFIX);
};
export const cleanCustomFontName = (fontName: string): string => {
  return fontName.replace(CUSTOM_FONT_PREFIX, '');
};

let isAppleCPUCached: boolean | undefined = undefined;
export const isAppleCPU = (): boolean => {
  if (isAppleCPUCached !== undefined) {
    return isAppleCPUCached;
  }
  // detect arm: https://stackoverflow.com/questions/65146751/detecting-apple-silicon-mac-in-javascript
  var w = document.createElement('canvas').getContext('webgl');
  var d = w?.getExtension('WEBGL_debug_renderer_info');
  var g = (d && w?.getParameter(d.UNMASKED_RENDERER_WEBGL)) || '';
  isAppleCPUCached = !!(g.match(/Apple/) && !g.match(/Apple GPU/));
  return isAppleCPUCached;
};

export const canHover = (): boolean => {
  return window.matchMedia('(hover: hover)').matches;
};

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });

export const controllerIsVisible = (): boolean => {
  const controller = document.querySelector('.controller');
  if (controller === null) {
    return false;
  }
  return controller.getBoundingClientRect().y < 100;
};

export const isMobileLayout = (): boolean => {
  return window.matchMedia('(max-width: 599px)').matches;
};

export const iOSWrapperHasMessageHandler = (name: string): boolean => {
  return (window as any).webkit?.messageHandlers?.[name] !== undefined;
};

export const iOSWrapperHasMessageHandlers = (names: string[]): boolean => {
  for (const name of names) {
    if (!iOSWrapperHasMessageHandler(name)) {
      return false;
    }
  }
  return true;
};

export const postMessageToIosWrapper = (funcionName: string, data?: Object) => {
  (window as any).webkit?.messageHandlers?.[funcionName]?.postMessage(
    data ?? {}
  );
};

export const postMessageToIosWrapperWithResponse = (
  funcionName: string,
  data?: Object
): Promise<any> => {
  return new Promise(resolve => {
    const callbackName = `beatflyerWebkitCallback${funcionName}`;
    (window as any)[callbackName] = (result: string) => {
      (window as any)[callbackName] = undefined;
      resolve(JSON.parse(decodeURI(result)));
    };
    postMessageToIosWrapper(funcionName, {
      ...(data ?? {}),
      callbackName
    });
  });
};

export const getXFromPointerEvent = (e: MouseEvent | TouchEvent): number => {
  if (e instanceof MouseEvent) {
    return e.clientX;
  } else if (e instanceof TouchEvent) {
    return e.touches[0].clientX;
  }
  console.error('Event is neither MouseEvernt nor TouchEvent');
  return 0;
};

export const formatTimestamp = (ms: number): string => {
  const minutes = Math.floor(ms / 60_000);
  ms -= minutes * 60_000;
  const seconds = Math.floor(ms / 1_000);
  ms -= seconds * 1_000;
  const cents = Math.floor(ms / 10);
  return [minutes, seconds, cents]
    .map(n =>
      n.toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false
      })
    )
    .join(':');
};

export const getCloneBaseId = (id: string): string => {
  if (isUUID(id)) {
    return id;
  }
  const arr = id.split('-');
  if (arr.length > 1 && !isNaN(parseInt(arr[arr.length - 1], 10))) {
    arr.pop();
    id = arr.join('-');
  }
  return id;
};

export const getCloneId = (id: string, copyIndex: number) => {
  return `${getCloneBaseId(id)}-${copyIndex}`;
};

export const isValidEmail = (email: string): boolean => {
  const emailReg =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailReg.test(email.trim());
};



// WEBPACK FOOTER //
// ./src/utils/index.ts